/**
 * Permisos de la sesión sobre árboles y tenants (funciones puras, sin imports).
 *
 * La autoridad es la API: is-verify entrega en cada asignación `canRegister` y `canUpdate` ya resueltos
 * (visitante: solo lectura; investigador: según su asignación; admin: todo). Aquí solo se decide qué
 * ofrecer en la interfaz, y se concede únicamente con `=== true`: si falta un dato, no se ofrece.
 *
 * session = { isAuthenticated, isAdmin, assignedTenants }
 * tenantRef = { tenantId, tenantSlug }
 */

export const ROLE = Object.freeze({
  ADMIN: 'administrador',
  RESEARCHER: 'investigador',
  VISITOR: 'visitante',
})

// Coinciden con los campos de la API en assignedTenants y en las asignaciones de administración.
export const TREE_PERMISSION = Object.freeze({ REGISTER: 'canRegister', UPDATE: 'canUpdate' })

export const TREE_DETAIL_TYPE = Object.freeze({ FULL: 'full' })

export const API_ERROR = Object.freeze({
  DETAIL_NOT_ALLOWED: 'detailNotAllowed',
  ACCOUNT_DISABLED: 'accountDisabled',
})

export const ASSIGNMENT_ACCESS_MODE = Object.freeze({
  TOTAL: 'total',
  FLAGS: 'flags',
  READ_ONLY: 'readOnly',
  NONE: 'none',
})

const HTTP_FORBIDDEN = 403
const HTTP_UNAUTHORIZED = 401

const hasValue = value => value !== null && value !== undefined && value !== ''

const toAssignmentList = assignedTenants => (Array.isArray(assignedTenants) ? assignedTenants : [])

/** Primer tenantId y primer tenantSlug con valor entre las fuentes de propiedades, en el orden dado. */
export const getTreeTenantRef = (...propertySources) => {
  const sources = propertySources.filter(Boolean)
  const sourceWithId = sources.find(source => hasValue(source.tenantId))
  const sourceWithSlug = sources.find(source => hasValue(source.tenantSlug))
  return {
    tenantId: sourceWithId ? sourceWithId.tenantId : null,
    tenantSlug: sourceWithSlug ? sourceWithSlug.tenantSlug : null,
  }
}

/**
 * Asignación de la sesión para un tenant. Si se conoce el id, decide el id (el slug del GeoJSON sale del
 * caché y puede ir atrasado tras una reasignación); si no, el slug. Sin ninguno de los dos, ninguna.
 */
export const findTenantAssignment = (assignedTenants, tenantRef) => {
  const assignments = toAssignmentList(assignedTenants)
  if (hasValue(tenantRef?.tenantId)) {
    return assignments.find(assignment => String(assignment.tenantId) === String(tenantRef.tenantId)) || null
  }
  if (hasValue(tenantRef?.tenantSlug)) {
    return assignments.find(assignment => assignment.tenantSlug === tenantRef.tenantSlug) || null
  }
  return null
}

export const hasTenantPermission = (assignment, permission) => assignment?.[permission] === true

/** Ficha completa: admin, o cualquier asignación al tenant del árbol (visitante o investigador). */
export const canViewTreeDetail = (session, tenantRef) => {
  if (!session?.isAuthenticated) return false
  if (session.isAdmin) return true
  return findTenantAssignment(session.assignedTenants, tenantRef) !== null
}

const canActOnTenant = (session, tenantRef, permission) => {
  if (!session?.isAuthenticated) return false
  if (session.isAdmin) return true
  return hasTenantPermission(findTenantAssignment(session.assignedTenants, tenantRef), permission)
}

export const canUpdateTree = (session, tenantRef) =>
  canActOnTenant(session, tenantRef, TREE_PERMISSION.UPDATE)

/** ¿Tiene el permiso en algún tenant del cantón? Decide por el cantón de cada asignación (cantonSlug de is-verify). */
export const canActInCanton = (session, permission, cantonSlug) => {
  if (!session?.isAuthenticated) return false
  if (session.isAdmin) return true
  return toAssignmentList(session.assignedTenants).some(
    assignment => assignment.cantonSlug === cantonSlug && hasTenantPermission(assignment, permission),
  )
}

/** ¿Tiene el permiso en algún tenant? (p. ej. para mostrar "Registrar árbol" en el menú). */
export const canActOnAnyTenant = (session, permission) => {
  if (!session?.isAuthenticated) return false
  if (session.isAdmin) return true
  return toAssignmentList(session.assignedTenants).some(assignment =>
    hasTenantPermission(assignment, permission),
  )
}

/** Cantón al que llevar al usuario para el permiso: el de su asignación principal con ese permiso, o el primero. */
export const getPreferredCantonSlug = (assignedTenants, permission) => {
  const permitted = toAssignmentList(assignedTenants).filter(
    assignment => hasValue(assignment.cantonSlug) && hasTenantPermission(assignment, permission),
  )
  const preferred = permitted.find(assignment => assignment.isPrimary) || permitted[0]
  return preferred ? preferred.cantonSlug : null
}

/** Tenants del cantón (CitiesContext: { id, slug, ... }) en los que la sesión tiene el permiso. */
export const getPermittedTenants = (session, cantonTenants, permission) => {
  const tenants = Array.isArray(cantonTenants) ? cantonTenants : []
  if (!session?.isAuthenticated) return []
  if (session.isAdmin) return tenants
  return tenants.filter(tenant =>
    hasTenantPermission(
      findTenantAssignment(session.assignedTenants, { tenantId: tenant.id, tenantSlug: tenant.slug }),
      permission,
    ),
  )
}

export const isFullTreeDetail = properties => properties?._dataType === TREE_DETAIL_TYPE.FULL

/** La API negó la ficha completa a esta sesión (403 detailNotAllowed). */
export const isDetailNotAllowedError = error =>
  error?.response?.status === HTTP_FORBIDDEN &&
  error?.response?.data?.error === API_ERROR.DETAIL_NOT_ALLOWED

/** El token de la sesión ya no es válido (vencido o firmado con otro secreto): 403 con auth:false. */
export const isInvalidSessionError = error =>
  error?.response?.status === HTTP_FORBIDDEN && error?.response?.data?.auth === false

/** La API rechazó la sesión porque la cuenta ya no está habilitada (401 accountDisabled). */
export const isAccountDisabledError = error =>
  error?.response?.status === HTTP_UNAUTHORIZED &&
  error?.response?.data?.error === API_ERROR.ACCOUNT_DISABLED

/** ¿Las asignaciones guardadas ya traen los flags? (una sesión de localStorage anterior al cambio no los trae). */
export const hasPermissionFlags = assignedTenants =>
  toAssignmentList(assignedTenants).every(
    assignment =>
      typeof assignment?.canRegister === 'boolean' && typeof assignment?.canUpdate === 'boolean',
  )

/** Cómo se administran los permisos de una asignación según el rol del usuario. Rol desconocido: con flags. */
export const getAssignmentAccessMode = roleType => {
  if (roleType === ROLE.ADMIN) return ASSIGNMENT_ACCESS_MODE.TOTAL
  if (roleType === ROLE.VISITOR) return ASSIGNMENT_ACCESS_MODE.READ_ONLY
  if (roleType === ROLE.RESEARCHER || !hasValue(roleType)) return ASSIGNMENT_ACCESS_MODE.FLAGS
  return ASSIGNMENT_ACCESS_MODE.NONE
}
